import { UserProfession } from '~/enums/UserProfession';
import { type IShiftDetailsFormValues } from '~/components/ShiftRequestRedesign/ShiftDetails/ShiftDetailsForm/useShiftDetailsForm';
import { type IAverageRating } from '~/domain/Jobs/types/averageRateTypes';

export interface IRequestRateDetails {
  rate: number | null;
  lowestShiftRate: number | null;
  recommendedRate: number | null;
  profession: string | null;
  rateGap: number | null;
}

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const resolveLowestShiftRate = (formValues: IShiftDetailsFormValues): number | null => {
  const shiftRates = formValues.dates
    .map((date) => formValues.shiftOverrides?.[date.toISOString()]?.rate ?? formValues.rate)
    .filter(isFiniteNumber);

  return shiftRates.length > 0 ? Math.min(...shiftRates) : null;
};

// Not the form's recommendedRate: that holds the GOTU-972 enhanced rate in its test arm.
export const resolveRequestRateDetails = (
  formValues: IShiftDetailsFormValues,
  averageRating?: IAverageRating,
): IRequestRateDetails => {
  const profession: string | null = UserProfession.getCode(formValues.professionType) ?? null;
  const average = profession ? averageRating?.[profession]?.average : undefined;
  // An average of 0 or less is not a market rate; a gap against it would skew the readout.
  const recommendedRate = isFiniteNumber(average) && average > 0 ? Math.round(average) : null;
  const lowestShiftRate = resolveLowestShiftRate(formValues);

  return {
    rate: formValues.rate,
    lowestShiftRate,
    recommendedRate,
    profession,
    rateGap:
      lowestShiftRate !== null && recommendedRate !== null
        ? lowestShiftRate - recommendedRate
        : null,
  };
};
